import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

export const AuthContext = createContext();

const URL = "https://sqlguroo.com";

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(
    () => localStorage.getItem("userId") || null
  );
  const [firstName, setFirstName] = useState(
    () => localStorage.getItem("firstName") || ""
  );
  const [lastName, setLastName] = useState(
    // Added lastName
    () => localStorage.getItem("lastName") || ""
  );
  const [username, setUsername] = useState(
    () => localStorage.getItem("username") || ""
  );
  const [paidUser, setPaidUser] = useState(
    () => localStorage.getItem("paidUser") || ""
  );

  // Enhanced subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(
    () => localStorage.getItem("subscriptionStatus") || null
  );
  const [userType, setUserType] = useState(
    () => localStorage.getItem("userType") || "anonymous"
  );
  const [subscriptionData, setSubscriptionData] = useState(() => {
    const saved = localStorage.getItem("subscriptionData");
    return saved ? JSON.parse(saved) : null;
  });

  // Regular login
  const login = async (email, password) => {
    const response = await axios.post(
      `${URL}/auth/login`,
      { username: email, password },
      { withCredentials: true }
    );

    const {
      id,
      firstName: userFirstName,
      lastName: userLastName,
      username,
      paidUser,
    } = response.data;
    updateAuthState(id, userFirstName, userLastName, username, paidUser);
  };

  // Google login handler
  const handleGoogleCallback = async () => {
    try {
      const response = await axios.get(`${URL}/auth/google/success`, {
        withCredentials: true,
      });

      const { id, firstName, lastName, username, paidUser } = response.data;
      updateAuthState(id, firstName, lastName, username, paidUser);
    } catch (error) {
      console.error("Error handling Google callback:", error);
      throw error;
    }
  };

  // Function to fetch subscription data
  const fetchSubscriptionData = async (userId) => {
    try {
      const response = await axios.get(
        `${URL}/subapi/user-subscription?userId=${userId}`,
        { withCredentials: true }
      );

      if (response.data.success && response.data.data.length > 0) {
        const userData = response.data.data[0];
        const subscription = userData.status ? userData : null;
        const status = subscription?.status || null;

        // Determine user type based on subscription status
        let type = "basic"; // default for logged-in users
        if (status === "active") {
          type = "premium";
        }

        // Update subscription state
        setSubscriptionStatus(status);
        setUserType(type);
        setSubscriptionData(subscription);

        // Store in localStorage
        localStorage.setItem("subscriptionStatus", status || "");
        localStorage.setItem("userType", type);
        localStorage.setItem("subscriptionData", JSON.stringify(subscription));

        return { status, type, subscription };
      } else {
        // No subscription found - basic user
        setSubscriptionStatus(null);
        setUserType("basic");
        setSubscriptionData(null);

        localStorage.setItem("subscriptionStatus", "");
        localStorage.setItem("userType", "basic");
        localStorage.setItem("subscriptionData", "null");

        return { status: null, type: "basic", subscription: null };
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      // Fallback to basic user on error
      setSubscriptionStatus(null);
      setUserType("basic");
      setSubscriptionData(null);

      localStorage.setItem("subscriptionStatus", "");
      localStorage.setItem("userType", "basic");
      localStorage.setItem("subscriptionData", "null");

      return { status: null, type: "basic", subscription: null };
    }
  };

  // Helper function to update auth state
  const updateAuthState = async (
    id,
    firstName,
    lastName,
    username,
    paidUser
  ) => {
    setUserId(id);
    setFirstName(firstName);
    setLastName(lastName); // Added lastName
    setUsername(username);
    setPaidUser(paidUser);

    // Store in localStorage
    localStorage.setItem("userId", id);
    localStorage.setItem("firstName", firstName);
    localStorage.setItem("lastName", lastName); // Added lastName
    localStorage.setItem("username", username);
    localStorage.setItem("paidUser", paidUser);

    // Fetch subscription data for the user
    if (id) {
      await fetchSubscriptionData(id);
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${URL}/auth/logout`,
        {},
        {
          withCredentials: true,
        }
      );
      clearAuthState(true); // Redirect after intentional logout
    } catch (error) {
      console.error("Error during logout:", error);
      // Force clear cookies even if server logout fails
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      clearAuthState(true); // Redirect after logout attempt
    }
  };

  // Add clearAuthState as a useCallback to prevent recreation
  const clearAuthState = useCallback((shouldRedirect = false) => {
    // Clear all auth state
    setUserId(null);
    setFirstName("");
    setLastName("");
    setUsername("");
    setPaidUser("");

    // Clear subscription state
    setSubscriptionStatus(null);
    setUserType("anonymous");
    setSubscriptionData(null);

    localStorage.removeItem("userId");
    localStorage.removeItem("firstName");
    localStorage.removeItem("lastName");
    localStorage.removeItem("username");
    localStorage.removeItem("paidUser");
    localStorage.removeItem("subscriptionStatus");
    localStorage.removeItem("userType");
    localStorage.removeItem("subscriptionData");

    // Only redirect if explicitly requested AND not already on login page
    if (shouldRedirect && !window.location.pathname.includes("/login")) {
      window.location.href = "/login";
    }
  }, []);

  // Update the useEffect with proper dependencies and guards
  useEffect(() => {
    let isMounted = true;

    const checkAuthStatus = async () => {
      // Don't check if we're already on the login page
      if (window.location.pathname.includes("/login")) {
        setIsLoading(false);
        return;
      }

      // Note: We don't clear redirectAfterLogin here because the backend handles redirect logic
      // The localStorage value will be cleared when the user successfully reaches their destination

      try {
        setIsLoading(true);
        const response = await axios.get(`${URL}/auth/status`, {
          withCredentials: true,
        });

        if (!isMounted) return;

        if (response.data.isAuthenticated && response.data.id) {
          const {
            id,
            firstName,
            lastName,
            username,
            paidUser,
            subscriptionStatus,
            userType,
            subscriptionData,
          } = response.data;

          // Update basic auth state
          setUserId(id);
          setFirstName(firstName);
          setLastName(lastName);
          setUsername(username);
          setPaidUser(paidUser);

          // Update subscription state from enhanced auth status
          setSubscriptionStatus(subscriptionStatus || null);
          setUserType(userType || "basic");
          setSubscriptionData(subscriptionData || null);

          // Store in localStorage
          localStorage.setItem("userId", id);
          localStorage.setItem("firstName", firstName);
          localStorage.setItem("lastName", lastName);
          localStorage.setItem("username", username);
          localStorage.setItem("paidUser", paidUser);
          localStorage.setItem("subscriptionStatus", subscriptionStatus || "");
          localStorage.setItem("userType", userType || "basic");
          localStorage.setItem(
            "subscriptionData",
            JSON.stringify(subscriptionData || null)
          );
        } else {
          // Don't redirect for anonymous users - just clear state
          clearAuthState(false);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error checking auth status:", error);
        // Don't redirect on network errors - just clear state
        clearAuthState(false);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuthStatus();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [clearAuthState]); // Add clearAuthState to dependencies

  return (
    <AuthContext.Provider
      value={{
        userId,
        firstName,
        lastName, // Added lastName
        username,
        paidUser,
        isLoading, // Add loading state to context
        // Enhanced subscription state
        subscriptionStatus,
        userType,
        subscriptionData,
        // Functions
        login,
        logout,
        handleGoogleCallback,
        fetchSubscriptionData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
